import { useEffect, useRef, useState } from 'react';

import { cloneDeep, isEqual } from 'lodash-es';
import styled from 'styled-components';

import { Card } from '@components/card';
import { CardList, CardListHeader, CardListItems } from '@components/card-list';
import { useContainerSize } from '@components/component-helpers';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconTrash } from '@components/icons/trash';
import { IconVisibilityHidden } from '@components/icons/visibility-hidden';
import { IconVisibilityShown } from '@components/icons/visibility-shown';
import { Menu } from '@components/menu';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { CareerSiteLanguageType, careerSitelanguages } from '@locale/locale-store';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { useDeleteLanguage, useUpdateJob } from 'api/jobs/hooks';
import { LanguageData } from 'api/jobs/requests';
import { FLAG_MAPPER } from 'components/iconMappers';
import { useModal } from 'features/modals';
import { useStore } from 'models/Provider';
import { Job, JobLanguage } from 'types/jobs';

import { getMissingApplicationFormTranslations, getMissingJobPostingTranslations } from './helpers';

export const useJobLanguages = (job?: Job, jobLanguages?: CareerSiteLanguageType[]) => {
  const { current } = useStore();
  const deleteLanguage = useDeleteLanguage();
  const [languages, setLanguages] = useState<CareerSiteLanguageType[]>(
    jobLanguages ??
      (job ? job?.languages.map((l: JobLanguage) => l.language) : [current.info.defaultLanguage])
  );
  const updateJob = useUpdateJob();

  const getLanguageStatus = () =>
    job
      ? job.languages.map((l) => ({
          code: l.language,
          isEnabled: l.isEnabled,
          hasApplicants: false,
        }))
      : [
          {
            code: current.info.defaultLanguage,
            isEnabled: true,
            hasApplicants: false,
          },
        ];
  const originalLanguageStatus = useRef<LanguageData[]>(getLanguageStatus());
  const [languageStatus, setLanguageStatus] = useState<LanguageData[]>(
    originalLanguageStatus.current
  );
  const noLangEnabled = languageStatus.every((l) => l.isEnabled === false);
  const isDirtyLanguages = !isEqual(languageStatus, originalLanguageStatus.current);

  useEffect(() => {
    originalLanguageStatus.current = job
      ? job.languages.map((l) => ({
          code: l.language,
          isEnabled: l.isEnabled,
          hasApplicants: false,
        }))
      : [
          {
            code: current.info.defaultLanguage,
            isEnabled: true,
            hasApplicants: false,
          },
        ];
    setLanguageStatus(cloneDeep(originalLanguageStatus.current));
  }, [current.info.defaultLanguage, job]);

  const resetLanguages = () => {
    setLanguageStatus(cloneDeep(originalLanguageStatus.current));
    setLanguages(
      jobLanguages ??
        (job ? job?.languages.map((l: JobLanguage) => l.language) : [current.info.defaultLanguage])
    );
  };

  const setIsEnabled = (code: CareerSiteLanguageType, isEnabled: boolean) => {
    const newLanguageStatus = [...languageStatus];
    const index = newLanguageStatus.findIndex((l) => l.code === code);
    newLanguageStatus[index].isEnabled = isEnabled;
    setLanguageStatus(newLanguageStatus);
  };

  const onAddLanguage = (code: CareerSiteLanguageType) => {
    setLanguages([...languages, code]);
    const newLanguageStatus = [...languageStatus];
    newLanguageStatus.push({
      code,
      isEnabled: languages.length === 0 ? true : false,
      hasApplicants: false,
    });
    setLanguageStatus(newLanguageStatus);
  };

  const onRemoveLanguage = async (
    code: CareerSiteLanguageType,
    discardJobQuestions?: (code: CareerSiteLanguageType) => void
  ) => {
    const enabledLanguagesInState = languageStatus
      .filter((l) => l.code !== code && l.isEnabled)
      .map((l) => l.code);

    const languagesChangedToEnabled = job?.languages
      .filter((l) => !l.isEnabled)
      .filter((l) => enabledLanguagesInState.includes(l.language))
      .map((l) => l.language);

    if (job && languagesChangedToEnabled && languagesChangedToEnabled.length > 0) {
      const data = {
        languages: job?.languages.map((l) => ({
          ...l,
          isEnabled: languagesChangedToEnabled?.includes(l.language) || l.isEnabled,
        })),
      };
      await updateJob.mutateAsync({ jobId: job.id, job: data });
    }
    let shouldUpdate = true;
    if (job && job.languages.find((l) => l.language === code)) {
      const deleted = await deleteLanguage.mutateAsync({
        jobId: job.id,
        langKey: code,
      });
      shouldUpdate = !deleted.error;
    }
    if (shouldUpdate) {
      setLanguageStatus(languageStatus.filter((lang) => lang.code !== code));
      setLanguages(languages.filter((lang) => lang !== code));
      if (discardJobQuestions) {
        discardJobQuestions(code);
      }
    }
  };

  const missingTranslations = job && getMissingJobPostingTranslations(job, languages);

  const anyMissingTranslations =
    job &&
    missingTranslations &&
    Object.values(missingTranslations).some((value) => value === true);

  const missingTranslationsApplicationForm =
    job && getMissingApplicationFormTranslations(job, languages);

  const languageBoxProps = {
    currentLangs: current.langs,
    languages,
    languageStatus,
    missingTranslations,
    missingTranslationsElsewhere: missingTranslationsApplicationForm,
    onEnable: (code: CareerSiteLanguageType) => setIsEnabled(code, true),
    onDisable: (code: CareerSiteLanguageType) => setIsEnabled(code, false),
    onAdd: onAddLanguage,
    onRemove: onRemoveLanguage,
  };

  return {
    languages,
    languageStatus,
    noLangEnabled,
    isDirtyLanguages,
    missingTranslations,
    anyMissingTranslations,
    resetLanguages,
    LanguageBox,
    languageBoxProps,
    onAddLanguage,
  };
};

type LanguageBoxType = {
  currentLangs: CareerSiteLanguageType[];
  languages: CareerSiteLanguageType[];
  languageStatus: { code: CareerSiteLanguageType; isEnabled: boolean; hasApplicants: boolean }[];
  missingTranslations?: { [key: string]: boolean };
  missingTranslationsElsewhere?: { [key: string]: boolean };
  onEnable: (code: CareerSiteLanguageType) => void;
  onDisable: (code: CareerSiteLanguageType) => void;
  onAdd: (code: CareerSiteLanguageType) => void;
  onRemove: (
    code: CareerSiteLanguageType,
    discardJobQuestions?: (code: CareerSiteLanguageType) => void
  ) => void;
  discardJobQuestions?: (code: CareerSiteLanguageType) => void;
};

export const LanguageBox = (props: LanguageBoxType) => {
  const {
    currentLangs,
    languages,
    languageStatus,
    missingTranslations,
    missingTranslationsElsewhere,
    onEnable,
    onDisable,
    onAdd,
    onRemove,
    discardJobQuestions,
  } = props;
  const { t } = useScopedTranslate(languageBoxLocale);
  const { showModal } = useModal();

  const SelectableLanguage = ({
    code,
    display,
  }: {
    code: CareerSiteLanguageType;
    display: string;
  }) => {
    return (
      <>
        {currentLangs.includes(code) && !languages.includes(code) && (
          <Menu.Item onSelect={() => onAdd(code)} label={display} icon={FLAG_MAPPER[code]} />
        )}
      </>
    );
  };

  const onRemoveLanguage = (code: CareerSiteLanguageType) => {
    showModal('CONFIRM_MODAL', {
      confirm: () => {
        discardJobQuestions ? onRemove(code, discardJobQuestions) : onRemove(code);
        return true;
      },
      data: {
        id: code,
      },
      buttonIcon: <IconTrash />,
      headerText: t('removeLanguageTitle'),
      mainText: t('removeLanguageConfirmation'),
      dangerText: t('canNotBeUndone'),
      confirmText: t('remove'),
      negative: true,
    });
  };

  const hasOnlyOneLanguageEnabled = languageStatus.filter((lang) => lang.isEnabled).length === 1;

  const cardListItemsRef = useRef<HTMLDivElement>(null);
  const cardListItemsSize = useContainerSize(cardListItemsRef);

  return (
    <LanguageSection>
      <CardList>
        <CardListHeader label={t('languages')}>
          {currentLangs.some((lang: CareerSiteLanguageType) => !languages.includes(lang)) && (
            <Menu
              placement="bottom-start"
              buttonProps={{ iconLeft: <IconAddPlus />, variant: 'ghost' }}
            >
              <Menu.Label label={t('add')} />
              <Menu.Separator />
              {careerSitelanguages.map((language) => (
                <SelectableLanguage code={language} display={t(language)} key={language} />
              ))}
            </Menu>
          )}
        </CardListHeader>
        <CardListItems ref={cardListItemsRef}>
          {languages.map((language) => {
            const langTranslationMissing = missingTranslations && missingTranslations[language];
            const isEnableDisabled =
              (missingTranslationsElsewhere && missingTranslationsElsewhere[language]) ||
              langTranslationMissing;
            const isRemoveDisabled =
              hasOnlyOneLanguageEnabled &&
              languageStatus.filter((lang) => lang.isEnabled)[0].code === language;
            const languageObject = languageStatus.find((lang) => lang.code === language);
            const hideLanguageText =
              cardListItemsSize.width <= 270 &&
              (langTranslationMissing || !languageObject?.isEnabled);
            return (
              <Card spaceX={4} spaceY={2} key={language}>
                <Stack orientation="horizontal" align="center" justify="space-between">
                  <Stack space={3} orientation="horizontal" align="center">
                    {FLAG_MAPPER[language]}
                    {!hideLanguageText && <Text text={t(`${language}`)} font="semibold14" />}
                  </Stack>
                  <Stack orientation="horizontal" align="center" justify="center">
                    <Stack orientation="horizontal" align="center" justify="center">
                      {langTranslationMissing && (
                        <IconSpacer align="center" justify="center">
                          <IconFormIndicatorError
                            color="red100"
                            tooltip={t('finishTranslations')}
                          />
                        </IconSpacer>
                      )}
                      {!languageObject?.isEnabled && (
                        <IconSpacer align="center" justify="center">
                          <StyledIconVisibilityHidden
                            color="purple50"
                            tooltip={t('languageNotPublished')}
                          />
                        </IconSpacer>
                      )}
                    </Stack>
                    <Menu
                      placement="bottom-start"
                      buttonProps={{ iconLeft: <IconMoreVertical />, variant: 'ghost' }}
                    >
                      {languageObject?.isEnabled ? (
                        <Menu.Item
                          onSelect={() => onDisable(language)}
                          label={t('disable')}
                          icon={<IconVisibilityHidden />}
                          isDisabled={hasOnlyOneLanguageEnabled}
                          tooltip={
                            hasOnlyOneLanguageEnabled ? t('disableLanguageTooltip') : undefined
                          }
                        />
                      ) : (
                        <Menu.Item
                          onSelect={() => onEnable(language)}
                          label={t('enable')}
                          icon={<IconVisibilityShown />}
                          isDisabled={isEnableDisabled}
                          tooltip={isEnableDisabled ? t('finishTranslations') : undefined}
                        />
                      )}
                      {!languageObject?.hasApplicants && (
                        <Menu.Item
                          onSelect={() => onRemoveLanguage(language)}
                          label={t('removeLanguage')}
                          icon={<IconTrash />}
                          variant="danger"
                          isDisabled={isRemoveDisabled}
                          tooltip={isRemoveDisabled ? t('disableLanguageTooltip') : undefined}
                        />
                      )}
                    </Menu>
                  </Stack>
                </Stack>
              </Card>
            );
          })}
        </CardListItems>
      </CardList>
    </LanguageSection>
  );
};

const StyledIconVisibilityHidden = styled(IconVisibilityHidden)({
  '&:hover': {
    color: theme.color.purple80,
  },
});

const IconSpacer = styled(Stack)({
  width: pxToRem(40),
});

const LanguageSection = styled.div({
  position: 'sticky',
  top: 0,
});

const languageBoxLocale = {
  en: {
    languages: 'Languages',
    en: 'English',
    is: 'Icelandic',
    pl: 'Polish',
    no: 'Norwegian',
    sv: 'Swedish',
    fr: 'French',
    enable: 'Enable language',
    disable: 'Disable language',
    removeLanguage: 'Remove language',
    add: 'Add language',
    hasErrors: 'Has errors that need to be resolved',
    removeLanguageTitle: 'Remove language',
    removeLanguageConfirmation:
      'Are you sure you want to remove this language?\nAll translations will be lost.',
    remove: 'Remove',
    finishTranslations: 'Translations missing for this language',
    disableLanguageTooltip: 'Job requires at least one language',
    languageNotPublished: 'Language not published',
    canNotBeUndone: '\nThis can not be undone',
  },
  is: {
    languages: 'Tungumál',
    en: 'Enska',
    is: 'Íslenska',
    pl: 'Pólska',
    no: 'Norska',
    sv: 'Sænska',
    fr: 'Franska',
    enable: 'Virkja tungumál',
    disable: 'Gera tungumál óvirkt',
    removeLanguage: 'Fjarlægja tungumál',
    add: 'Bæta við tungumáli',
    hasErrors: 'Tungumálið inniheldur villur sem þarf að laga',
    removeLanguageTitle: 'Fjarlægja tungumál',
    removeLanguageConfirmation:
      'Ertu viss um að þú viljir fjarlægja þetta tungumál? Allar þýðingar munu eyðast.',
    remove: 'Fjarlægja',
    finishTranslations: 'Þýðingar vantar fyrir þetta tungumál',
    disableLanguageTooltip: 'Nauðsynlegt er að hafa að minnsta kosti eitt tungumál',
    languageNotPublished: 'Tungumál óvirkt',
    canNotBeUndone: '\nEkki er mögulegt að afturkalla þessa aðgerð',
  },
};
